import { Component } from "react";
import type { ReactNode, ErrorInfo } from "react";
import { Link, useRouteError } from "react-router-dom";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary that catches rendering crashes and shows a recovery UI
 * instead of a blank screen. Important for resilience — a single component
 * crash shouldn't take down the whole app.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("ErrorBoundary caught:", error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="error-boundary">
          <h2>Something went wrong</h2>
          <p className="error-boundary-detail">
            {this.state.error?.message ?? "An unexpected error occurred."}
          </p>
          <button
            className="btn btn--primary"
            onClick={() => this.setState({ hasError: false, error: null })}
          >
            Try again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export function RouteErrorFallback() {
  const error = useRouteError();
  const message = error instanceof Error ? error.message : "An unexpected page error occurred.";
  console.error("Route error caught:", error);
  return (
    <main className="route-error">
      <h1>Page Error</h1>
      <p>{message}</p>
      <Link className="action-btn" to="/">
        Back to Home
      </Link>
    </main>
  );
}
